import { useCallback } from 'react';
import {
  OnApproveData,
  PayPalButtonCreateOrder,
  PayPalButtonOnError,
} from '@paypal/paypal-js';
import {
  AlertCode,
  CreatePayPalOrderPayload,
  CreatePayPalOrderResponse,
  PSP,
  ValidatePayPalCheckoutPayload,
} from 'shared/types/checkout';
import { sdk } from 'sdk';
import { getAccountId, setAlert, useAppDispatch, useAppSelector } from 'store';
import { getAlertCodeForError } from 'utils';
import { withCookieValidation } from 'utils/edx-auth';
import { getPriceDetailsFromCart } from 'utils/price';
import { useCart } from '../useCart';
import { useCheckout } from '../useCheckout';

const PAYPAL_TRANSACTION_CANCELLED_ERRORS = [
  'Detected popup close',
  'Window is closed, can not determine type',
];

export const usePayPalOptions = () => {
  const dispatch = useAppDispatch();
  const customerId = useAppSelector(getAccountId);

  const { data } = useCart();
  const { placeOrder } = useCheckout();

  const priceDetails = getPriceDetailsFromCart(data);

  const createPayPalOrder = useCallback(
    async (payload: CreatePayPalOrderPayload) => {
      const apiResponse = await withCookieValidation(() =>
        sdk.callAction<CreatePayPalOrderResponse>({
          actionName: `${PSP.PAYPAL}/createPayPalOrder`,
          payload,
        }),
      );

      if (apiResponse.isError) {
        return apiResponse;
      }
      return apiResponse.data;
    },
    [],
  );

  const onApprove = async (
    onApproveData: OnApproveData,
    shouldRetrySdnHit = false,
  ) => {
    const pspData: ValidatePayPalCheckoutPayload = {
      paypalOrderID: onApproveData.orderID,
    };

    const result = await placeOrder({
      cartID: data!.cartId!,
      lineItems: data!.lineItems!,
      priceDetails,
      paymentMethodType: (onApproveData as unknown as { paymentSource: string })
        .paymentSource,
      psp: PSP.PAYPAL,
      pspData,
      shouldRetrySdnHit,
    });

    if (result?.isError) {
      return result;
    }

    return { isError: false };
  };

  const createOrder: PayPalButtonCreateOrder = async () => {
    const apiResponse = await createPayPalOrder({
      cartID: data!.cartId!,
      amount: priceDetails.cartTotal!,
    });

    if (apiResponse.isError) {
      const errorCode = (apiResponse.error?.code as string) ?? '';
      const errorMessage =
        apiResponse.error?.message ??
        JSON.stringify(apiResponse.error) ??
        'unknown';

      console.error(
        `Error in createPayPalOrder extension for ${PSP.PAYPAL} for customer: ${customerId}
        with error code: ${errorCode} and error message: ${errorMessage}`,
      );
      const alertCode = getAlertCodeForError(apiResponse.error, PSP.PAYPAL);
      dispatch(setAlert({ code: alertCode }));

      throw new Error('handled_exception');
    }
    return apiResponse.paypalOrderID;
  };

  const onCancel = () => {
    dispatch(setAlert({ code: AlertCode.PAYPAL_TRANSACTION_CANCELLED }));
  };

  const onError: PayPalButtonOnError = (error) => {
    if (PAYPAL_TRANSACTION_CANCELLED_ERRORS.includes(error.message as string)) {
      // Treat these errors as user-initiated cancellations since they occur when
      // the PayPal popup is closed prematurely (e.g. before fully loading).
      // This aligns with the behavior of a normal cancellation.
      onCancel();
    } else if (error.message !== 'handled_exception') {
      dispatch(setAlert({ code: AlertCode.PAYPAL_PAYMENT_PROCESSING_FAILED }));
    }
  };

  return {
    createOrder,
    onApprove,
    onCancel,
    onError,
  };
};
